import React, { useState, useMemo } from 'react';

import { ICrystalItem, ICrystalsContent } from '../../../api/charts/crystals';
import { IChartDataContent } from '../../../api/charts/types';
import { useCrystalsData } from '../../../hooks/useCrystalsData';
import { IProfile } from '../../../types/Profile';
import PersonalContent from '../../PersonalContent';
import TabsContentPanel from '../../TabsContentPanel';

import { Crystals } from './Crystals';
import { CrystalsData, CrystalsTabType } from './types';

interface PersonalContentProps {
  activeProfile: IProfile;
}

/** Extra fields we want to keep per element (everything from API item except title/description) */
type CrystalElementExtra = Omit<ICrystalItem, 'title' | 'description'>;

/** Extra fields per content section (optional). Keep raw API if useful for debugging/links. */
type CrystalsSectionExtra = {
  raw?: ICrystalsContent;
};

export function CrystalsContent({ activeProfile }: PersonalContentProps) {
  const [crystals, setCrystals] = useState<CrystalsData>({ type: 'list', items: [] });

  const { data, isLoading } = useCrystalsData(activeProfile);

  /** Build IChartDataContent by mapping each leaf to IContent<CrystalElementExtra, CrystalsSectionExtra> */
  const chartContent: IChartDataContent<CrystalElementExtra, CrystalsSectionExtra> | null = useMemo(() => {
    if (!data) return null;

    return {
      personality_crystals: {
        power_crystals: data.personality_crystals.power_crystals,
        family_crystals: data.personality_crystals.family_crystals,
      },
      health_crystals: {
        health_crystals: data.health_crystals.health_crystals,
        safety_crystals: data.health_crystals.safety_crystals,
      },
      finance_crystals: {
        finance_crystals: data.finance_crystals.finance_crystals,
        career_crystals: data.finance_crystals.career_crystals,
      },
      relationships_crystals: {
        communication_and_training_crystals: data.relationships_crystals.communication_and_training_crystals,
        social_activity_crystals: data.relationships_crystals.social_activity_crystals,
      },
    };
  }, [data]);

  const tabs: { key: CrystalsTabType; label: string }[] = [
    { key: 'personality_crystals', label: 'Камни личности' },
    { key: 'health_crystals', label: 'Камни здоровья' },
    { key: 'finance_crystals', label: 'Камни финансов' },
    { key: 'relationships_crystals', label: 'Камни отношений' },
  ];

  const hasData = !!data && chartContent;

  return (
    <PersonalContent
      loading={isLoading}
      chart={<Crystals data={crystals} />}
      contentPanel={
        hasData ? (
          <TabsContentPanel<CrystalsTabType, CrystalElementExtra, CrystalsSectionExtra>
            tabs={tabs}
            chartType="crystalsChart"
            content={chartContent}
            onSelectionChange={({ element, elements }) => {
              if (element) {
                setCrystals({
                  type: 'detail',
                  item: {
                    id: element.id ?? element.title,
                    name: element.title,
                    description: element.description,
                    imageUrl: element.detailed_image_url,
                    compatibleCrystalsDescription: element.compatible_crystals_description ?? '',
                    compatibleMetals: element.compatible_metals ?? [],
                  },
                });
              } else {
                setCrystals({
                  type: 'list',
                  items: (elements ?? []).map((el, i) => ({
                    id: el.id ?? el.title ?? `crystal-${i}`,
                    name: el.title,
                    imageUrl: el.image_url,
                    detailedImageUrl: el.detailed_image_url,
                  })),
                });
              }
            }}
          />
        ) : null
      }
    />
  );
}
